import { useId, type ReactNode } from 'react';
import { Dialog, DialogContent, DialogClose, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { AlertIcon } from '../icons/AlertIcon';
import { cn } from '@/lib/utils';

interface ConfirmationDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => void;
  title: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  confirmingLabel?: string;
  isPending?: boolean;
  /** 'destructive' chilli-red, 'warning' brand-yellow, 'success' pale-mint, 'successSolid' filled mint */
  variant?: 'destructive' | 'warning' | 'success' | 'successSolid';
  /** Custom icon element. Defaults to AlertIcon */
  icon?: ReactNode;
  /** Additional class names for the dialog content container */
  className?: string;
  overlayClassName?: string;
  /** Override classes on the message paragraph (e.g. to widen past `max-w-confirm-dialog-message`). */
  messageClassName?: string;
  /** Rendered between the message and the actions — a standing caveat. */
  note?: ReactNode;
  /** Hide the Cancel + confirm action row entirely — for info-only
   *  prompts ("Can't confirm this order yet") where the user just
   *  needs to dismiss / acknowledge. */
  hideActions?: boolean;
  /** Inline styles for the dialog content container */
  style?: React.CSSProperties;
}

const VARIANT_STYLES = {
  destructive: { iconBg: 'bg-chilli-blush', iconColor: 'text-chilli-red', button: 'destructive-subtle' },
  warning: { iconBg: 'bg-custard-yellow', iconColor: 'text-brand-yellow', button: 'warning-subtle' },
  success: { iconBg: 'bg-yield-positive-bg', iconColor: 'text-yield-positive', button: 'success-subtle' },
  successSolid: { iconBg: 'bg-yield-positive-bg', iconColor: 'text-yield-positive', button: 'success-solid' },
} as const;

export function ConfirmationDialog({
  open,
  onOpenChange,
  onConfirm,
  title,
  message,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  confirmingLabel = 'Processing...',
  isPending = false,
  variant = 'destructive',
  icon,
  className,
  overlayClassName,
  messageClassName,
  note,
  hideActions = false,
  style,
}: ConfirmationDialogProps) {
  const styles = VARIANT_STYLES[variant];
  // Radix's aria-describedby covers the message alone, so describe the button.
  const noteId = useId();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        overlayClassName={overlayClassName}
        className={cn(
          // outline-none: with hideActions there is no focusable child, so Radix
          // focuses the panel itself — suppress the browser's default focus ring.
          'bg-off-white flex flex-col gap-8 items-end p-8 rounded-input sm:rounded-input border-none shadow-none outline-none w-confirm-dialog [&>button]:hidden',
          className,
        )}
        style={style}
      >
        <div className="flex flex-col gap-4 items-center justify-center shrink-0 w-full">
          <div className={`${styles.iconBg} rounded-full shrink-0 w-16 h-16 flex items-center justify-center`}>
            {icon ?? <AlertIcon className={`w-8 h-8 ${styles.iconColor}`} />}
          </div>
          <DialogTitle className="font-inter font-semibold leading-[1.5] tracking-normal text-lg text-off-black text-center text-balance">
            {title}
          </DialogTitle>
          <DialogDescription className={cn(
            'font-inter font-normal leading-[1.5] text-sm text-off-black text-center max-w-confirm-dialog-message mx-auto',
            messageClassName,
          )}>
            {message}
          </DialogDescription>
          {note ? <div id={noteId}>{note}</div> : null}
        </div>
        {!hideActions && (
          <div className="flex gap-4 items-center justify-center shrink-0 w-full">
            <DialogClose asChild>
              <Button variant="outline-subtle" size="dialog" disabled={isPending}>
                {cancelLabel}
              </Button>
            </DialogClose>
            <Button
              variant={styles.button}
              size="dialog"
              onClick={onConfirm}
              disabled={isPending}
              aria-describedby={note ? noteId : undefined}
            >
              {isPending ? confirmingLabel : confirmLabel}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
